import React, { useRef, useEffect } from 'react'
import * as THREE from 'three'
import { useMotionPreferences } from '../../../context/MotionPreferencesContext'
import { useTheme } from '../../../context/ThemeContext'
import styles from './WebGLBackground.module.css'

/**
 * WebGL Particle Background
 *
 * A transparent, full-viewport THREE.Points constellation drifting in 3D —
 * the "deep space" layer behind all page content. Mounted inside PageBackdrop,
 * it renders ON TOP of the CSS radial glows and UNDER the dotted grid/noise.
 *
 * Mirrors the imperative lifecycle of CanvasBackground.jsx: init in a deferred
 * rAF, run a rAF loop, observe resize, respect reduced-motion, pause when the
 * tab is hidden, and fully dispose all GPU resources on unmount.
 *
 * Particle colors are read from the theme's CSS custom properties
 * (--bg-particle / --bg-particle-2 / --primary-bg), so the cloud recolors when
 * the light/dark theme changes (the effect re-runs on `isDark`).
 *
 * CONFIGURATION (tune the vibe here):
 */
const CONFIG = {
  // Per-device particle tuning
  desktop: { count: 800, size: 7, antialias: true, parallax: true },
  mobile: { count: 300, size: 9, antialias: false, parallax: false },

  // Layout of the point cloud (world units)
  rangeXY: 1100, // half-extent of the box on X and Y
  rangeZ: 700, // half-extent on Z (depth)
  cameraZ: 1000, // camera distance from origin
  fov: 60,
  fogDensity: 0.00072, // FogExp2 density — distant points fade into the base

  // Motion (calm + ambient)
  rotationSpeed: 0.00002, // radians per ms of global cloud rotation
  bobAmplitude: 12, // gentle vertical drift (world units)
  parallaxAmp: { x: 60, y: 40 }, // camera sway from mouse (world units)
  parallaxLerp: 0.045, // how quickly the camera eases toward the target

  // Opacity of the additive sprite material per theme
  opacity: { dark: 0.95, light: 0.6 },

  // Fallback tints if the CSS variables can't be read
  fallback: { a: '#22d3ee', b: '#34d399', bg: '#0a0a0b' }
}

/**
 * Detects a touch / coarse-pointer device so we can drop particle count,
 * antialiasing and mouse parallax on mobile.
 */
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false
  const coarse = window.matchMedia?.('(pointer: coarse)').matches
  return Boolean(coarse) || window.innerWidth < 768
}

/**
 * Reads a CSS custom property and resolves it to a THREE.Color, falling back to
 * a hardcoded tint if the value is missing or unparseable.
 */
const readColor = (varName, fallback) => {
  const color = new THREE.Color()
  try {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim()
    if (raw) {
      color.setStyle(raw)
    } else {
      color.set(fallback)
    }
  } catch {
    color.set(fallback)
  }
  return color
}

/**
 * Generates a soft, round sprite texture (radial-gradient alpha) once on a tiny
 * 2D canvas. Used as the point map so each particle reads as a glowing dot.
 */
const createSpriteTexture = () => {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  const gradient = ctx.createRadialGradient(
    size / 2,
    size / 2,
    0,
    size / 2,
    size / 2,
    size / 2
  )
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)')
  gradient.addColorStop(0.2, 'rgba(255, 255, 255, 0.85)')
  gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.25)')
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, size, size)

  const texture = new THREE.CanvasTexture(canvas)
  texture.needsUpdate = true
  return texture
}

const WebGLBackground = () => {
  const containerRef = useRef(null)
  const { reducedMotion } = useMotionPreferences()
  const { isDark } = useTheme()

  // Rebuild the scene whenever motion preference or theme changes. Theme
  // changes need a rebuild so the baked per-vertex colors + fog recolor.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    // Disposable resources cleanup needs to reach (assigned inside `start`).
    let disposed = false
    let initId = 0
    let animationId = 0
    let renderer
    let geometry
    let material
    let texture
    let resizeObserver
    let canvas
    let onMouseMove
    let onVisibility
    let onContextLost

    const start = () => {
      if (disposed || !containerRef.current) return

      const mobile = isMobileDevice()
      const cfg = mobile ? CONFIG.mobile : CONFIG.desktop

      const width = container.clientWidth || window.innerWidth
      const height = container.clientHeight || window.innerHeight

      // Renderer — transparent so the CSS backdrop glows show through.
      renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: cfg.antialias,
        powerPreference: 'high-performance'
      })
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      renderer.setSize(width, height)
      renderer.setClearColor(0x000000, 0)

      canvas = renderer.domElement
      canvas.setAttribute('aria-hidden', 'true')
      canvas.classList.add(styles.canvas)
      container.appendChild(canvas)

      // Scene + fog (distant points fade into the resolved base color).
      const scene = new THREE.Scene()
      scene.fog = new THREE.FogExp2(
        readColor('--primary-bg', CONFIG.fallback.bg),
        CONFIG.fogDensity
      )

      const camera = new THREE.PerspectiveCamera(
        CONFIG.fov,
        width / height,
        1,
        4000
      )
      camera.position.z = CONFIG.cameraZ

      // Geometry — N points in a 3D box with per-vertex colors mixing two tints.
      const colorA = readColor('--bg-particle', CONFIG.fallback.a)
      const colorB = readColor('--bg-particle-2', CONFIG.fallback.b)
      const tint = new THREE.Color()

      const positions = new Float32Array(cfg.count * 3)
      const colors = new Float32Array(cfg.count * 3)

      for (let i = 0; i < cfg.count; i++) {
        const i3 = i * 3
        positions[i3] = (Math.random() * 2 - 1) * CONFIG.rangeXY
        positions[i3 + 1] = (Math.random() * 2 - 1) * CONFIG.rangeXY
        positions[i3 + 2] = (Math.random() * 2 - 1) * CONFIG.rangeZ

        tint.copy(colorA).lerp(colorB, Math.random())
        const brightness = 0.65 + Math.random() * 0.35
        colors[i3] = tint.r * brightness
        colors[i3 + 1] = tint.g * brightness
        colors[i3 + 2] = tint.b * brightness
      }

      geometry = new THREE.BufferGeometry()
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

      texture = createSpriteTexture()

      material = new THREE.PointsMaterial({
        size: cfg.size,
        map: texture,
        vertexColors: true,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true,
        opacity: isDark ? CONFIG.opacity.dark : CONFIG.opacity.light
      })

      const points = new THREE.Points(geometry, material)
      scene.add(points)

      const renderFrame = () => renderer.render(scene, camera)

      // Animation loop — slow rotation, gentle bob, eased mouse parallax.
      const mouse = { x: 0, y: 0 }
      let lastTime = performance.now()

      const loop = (time) => {
        const delta = Math.min(time - lastTime, 100)
        lastTime = time

        points.rotation.y += CONFIG.rotationSpeed * delta
        points.rotation.x = Math.sin(time * 0.00008) * 0.15
        points.position.y = Math.sin(time * 0.0002) * CONFIG.bobAmplitude

        if (cfg.parallax) {
          camera.position.x +=
            (mouse.x * CONFIG.parallaxAmp.x - camera.position.x) *
            CONFIG.parallaxLerp
          camera.position.y +=
            (mouse.y * CONFIG.parallaxAmp.y - camera.position.y) *
            CONFIG.parallaxLerp
          camera.lookAt(0, 0, 0)
        }

        renderFrame()
        animationId = requestAnimationFrame(loop)
      }

      // Resize — track the container, update camera + renderer.
      const onResize = () => {
        const w = container.clientWidth || window.innerWidth
        const h = container.clientHeight || window.innerHeight
        camera.aspect = w / h
        camera.updateProjectionMatrix()
        renderer.setSize(w, h)
        if (reducedMotion) renderFrame()
      }
      resizeObserver = new ResizeObserver(onResize)
      resizeObserver.observe(container)

      // Pause the loop when the tab is hidden.
      onVisibility = () => {
        if (reducedMotion) return
        if (document.hidden) {
          if (animationId) {
            cancelAnimationFrame(animationId)
            animationId = 0
          }
        } else if (!animationId) {
          lastTime = performance.now()
          animationId = requestAnimationFrame(loop)
        }
      }
      document.addEventListener('visibilitychange', onVisibility)

      // Recover gracefully if the GPU context is lost.
      onContextLost = (event) => {
        event.preventDefault()
        if (animationId) {
          cancelAnimationFrame(animationId)
          animationId = 0
        }
      }
      canvas.addEventListener('webglcontextlost', onContextLost, false)

      if (cfg.parallax) {
        onMouseMove = (event) => {
          mouse.x = (event.clientX / window.innerWidth) * 2 - 1
          mouse.y = -((event.clientY / window.innerHeight) * 2 - 1)
        }
        window.addEventListener('mousemove', onMouseMove, { passive: true })
      }

      // Reduced motion → a single static frame, no loop.
      if (reducedMotion) {
        renderFrame()
      } else {
        animationId = requestAnimationFrame(loop)
      }
    }

    // Defer setup one frame so the theme class (toggled by ThemeProvider's
    // effect, which runs AFTER this child effect) is applied before we read
    // the theme-dependent CSS color variables.
    initId = requestAnimationFrame(start)

    return () => {
      disposed = true
      if (initId) cancelAnimationFrame(initId)
      if (animationId) cancelAnimationFrame(animationId)
      if (onMouseMove) window.removeEventListener('mousemove', onMouseMove)
      if (onVisibility) {
        document.removeEventListener('visibilitychange', onVisibility)
      }
      if (canvas && onContextLost) {
        canvas.removeEventListener('webglcontextlost', onContextLost)
      }
      if (resizeObserver) resizeObserver.disconnect()
      if (geometry) geometry.dispose()
      if (material) material.dispose()
      if (texture) texture.dispose()
      if (renderer) {
        renderer.dispose()
        renderer.forceContextLoss?.()
      }
      if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas)
      }
    }
  }, [reducedMotion, isDark])

  return (
    <div ref={containerRef} className={styles.container} aria-hidden="true" />
  )
}

export default WebGLBackground
